import { useQuery } from '@tanstack/react-query';
import type { TaxGroup } from '@masamune/shared';
import { Icon } from '../../../components/ui/Icon';
import { Helper } from '../../../components/ui/Helper';
import { EmptyState } from '../../../components/ui/EmptyState';
import { fmtEur } from '../../../lib/utils';
import { plurale } from '../adminApi';
import { fiscoGruppoQuery, type PianoRate } from './fiscoApi';
import { Caricamento, Fascia, Frase, Intestazione, Piega, TabellaPagate, TabellaTasse } from './parts';

/**
 * Debiti a rate: i debiti vecchi già messi a posto con un piano (oggi il piano
 * dell'Agenzia Riscossione). La rata di ogni mese sta nella fascia e basta: le
 * 73 rate uguali ancora da pagare si dicono in una riga sola, senza ripetere
 * l'importo (SPEC, correzione 4: la rata una volta sola per schermata).
 */
export function RateView({ canWrite, onVai }: { canWrite: boolean; onVai: (g: TaxGroup) => void }) {
  const q = useQuery(fiscoGruppoQuery('RATE'));
  const d = q.data;
  if (q.isLoading && !d) return <Caricamento />;
  if (!d) return <EmptyState icon="calendarClock" title="Debiti a rate non disponibili" description="Non riesco a leggere i piani a rate in questo momento. Riprova tra poco." />;
  const piani = d.piani ?? [];
  const serie = d.serieChiuse ?? [];

  return (
    <>
      <Frase testo={d.frase} />
      <Fascia voci={d.fascia} />
      {piani.map((p) => <Piano key={p.obligationId} p={p} canWrite={canWrite} />)}
      {d.righe.length > 0 && (
        <section className="fis-gruppo mt-5">
          <Intestazione label="Altri debiti da pagare, fuori dai piani a rate" />
          <TabellaTasse righe={d.righe} canWrite={canWrite} onVai={onVai} />
        </section>
      )}
      {d.pagate.length > 0 && (
        <Piega label="gli altri debiti già pagati" n={d.pagate.length}>
          <TabellaPagate righe={d.pagate} canWrite={canWrite} />
        </Piega>
      )}
      {serie.length > 0 && (
        <Piega label="i vecchi piani a rate già finiti, trovati in banca" n={serie.length}>
          <div className="adm-note">
            <Icon name="help" size={12} className="mute" />
            <span>
              Serie di pagamenti uguali, uno al mese, che in banca risultano finiti. Probabilmente sono piani a rate già chiusi:
              qui non sono collegati a nessuna tassa, servono a rispondere a "abbiamo altri debiti a rate?".
            </span>
          </div>
          {serie.map((s) => (
            <div key={s.key} className="adm-line">
              <div className="min-w-0 flex-1">
                <span className="font-medium">{s.n} {s.tipo}</span>
                <span className="mute text-[12px]"> · {s.testo}</span>
              </div>
              <div className="mono text-right mute" style={{ width: 160 }}>€ {fmtEur(s.importo, true)} l'uno</div>
            </div>
          ))}
        </Piega>
      )}
    </>
  );
}

/** "dall'11 ottobre", "dal 12 ottobre", "all'8 marzo", "al 16 marzo". */
function conArticolo(prep: 'dal' | 'al', dataTesto: string): string {
  const g = Number(dataTesto.split(' ')[0]);
  return g === 1 || g === 8 || g === 11 ? `${prep}l'${dataTesto}` : `${prep} ${dataTesto}`;
}

function Piano({ p, canWrite }: { p: PianoRate; canWrite: boolean }) {
  const totaleDentro = p.dentro.reduce((s, x) => s + x.importo, 0);
  return (
    <>
      <section className="fis-gruppo mt-5">
        <Intestazione
          label={`Cosa c'è dentro il piano a rate${p.numero ? ` n. ${p.numero}` : ''}`}
          destra={p.dentro.length > 0 ? <><span className="mono mute text-[12px]">in tutto, interessi compresi</span><span className="mono fis-hd-tot">€ {fmtEur(totaleDentro, true)}</span></> : null}
        />
        {p.dentro.length === 0 ? (
          <div className="fis-vuoto">Nella nota del piano non c'è l'elenco dei debiti che contiene.</div>
        ) : p.dentro.map((x) => (
          <div key={x.cosa} className="adm-line">
            <div className="min-w-0 flex-1">{x.cosa}</div>
            <div className="mono font-semibold text-right" style={{ width: 140 }}>€ {fmtEur(x.importo, true)}</div>
          </div>
        ))}
      </section>
      <Piega label={`le ${p.rate} rate del piano, una per una`}>
        {p.ratePagate.length > 0 && (
          <>
            <Intestazione label="Rate del piano già pagate" destra={<span className="mono mute text-[12px]">{plurale(p.ratePagate.length, 'rata', 'rate')}</span>} />
            <TabellaPagate righe={p.ratePagate} canWrite={canWrite} importoUguale={p.rataMese} />
          </>
        )}
        {(p.rateDiverse.length > 0 || p.rateUguali) && (
          <div className="mt-5">
            <Intestazione label="Rate del piano da pagare" destra={<span className="mono mute text-[12px]">{plurale(p.rate - p.pagate, 'rata', 'rate')}</span>} />
            {p.rateDiverse.length > 0 && <TabellaTasse righe={p.rateDiverse} canWrite={canWrite} />}
            {p.rateUguali && (
              <div className="adm-line">
                <div className="min-w-0 flex-1">
                  <span className="font-medium">
                    Rate da {p.rateUguali.daN} a {p.rateUguali.aN}: {plurale(p.rateUguali.n, 'rata', 'rate')} uguali, una ogni mese
                  </span>
                  <span className="mute text-[12px]"> · {conArticolo('dal', p.rateUguali.primaTesto)} {conArticolo('al', p.rateUguali.ultimaTesto)}</span>
                  <Helper text="L'importo di ognuna è la rata del piano di ogni mese, scritto una volta sola nella fascia qui sopra." />
                </div>
              </div>
            )}
          </div>
        )}
      </Piega>
    </>
  );
}
